import React, { useEffect, useMemo, useState } from "react";
import { useTheme, alpha, getContrastRatio } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import RegistrationWizard from "../components/RegistrationWizard";
import { formatLocalDate } from "../utils/dateUtils";
import {
  fetchClassById,
  fetchRegistrationsForClass,
  fetchInstructorDashboard,
  fetchUsersByIds,
  fetchRegistrationsForUser,
  dropRegistration,
  exportToAuthority,
  fetchSessionsForCourse,
} from "../api/backend";
import {
  courseCatalogTokens as DS,
  getCategoryMeta,
  getHeroPalette,
} from "./courseCatalog/courseCatalogDesign";
import {
  TextField,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab,
  Card,
  Container,
  Stack,
  Chip,
  Checkbox,
  FormControlLabel,
  Paper,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";

// ─── Icons ────────────────────────────────────────────────────────────────────
function Svg({ d, size = 14, style, children, ...props }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} {...props}>
      {children}
    </svg>
  );
}

const IcoCal     = () => <Svg><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Svg>;
const IcoPin     = () => <Svg><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Svg>;
const IcoUser    = () => <Svg><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Svg>;
const IcoGrad    = () => <Svg size={14}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></Svg>;
const IcoClock   = () => <Svg><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Svg>;
const IcoTag     = () => <Svg><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></Svg>;
const IcoUsers   = () => <Svg><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></Svg>;
const IcoCheck   = () => <Svg size={13}><polyline points="20 6 9 17 4 12"/></Svg>;
const IcoInfo    = () => <Svg><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></Svg>;
const IcoList    = () => <Svg><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></Svg>;
const IcoLock    = () => <Svg size={13}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Svg>;
const IcoWarn    = () => <Svg><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Svg>;
const IcoCert    = () => <Svg><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Svg>;

function SectionHeader({ icon, children, id }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "11px", mb: "14px" }}>
      <Box sx={{
        width: "30px", height: "30px", borderRadius: "9px", flexShrink: 0,
        display: "grid", placeItems: "center", bgcolor: tPrimary10, color: tPrimary,
      }}>
        {icon}
      </Box>
      <Typography
        component="h2"
        id={id}
        sx={{
          m: 0,
          fontSize: "17px",
          fontWeight: 700,
          fontFamily: theme.typography.h2.fontFamily,
          letterSpacing: "-.01em",
          color: DS.ink,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function FactItem({ icon, label, children, palette }) {
  const theme = useTheme();
  return (
    <Box sx={{
      padding: "16px 22px",
      borderRight: { xs: 0, sm: `1px solid ${palette.line}` },
      borderBottom: { xs: `1px solid ${palette.line}`, md: 0 },
      display: "flex", flexDirection: "column", gap: "5px",
      "&:nth-of-type(2n)": { borderRight: { sm: 0, md: `1px solid ${palette.line}` } },
      "&:last-child": { borderRight: 0, borderBottom: 0 },
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: palette.mutedText }}>
        <Box sx={{ color: palette.mutedText, display: "inline-flex" }}>{icon}</Box>
        {label}
      </Box>
      <Box sx={{ fontSize: "15px", fontWeight: 700, color: palette.text, letterSpacing: "-.01em", fontFamily: theme.typography.h6.fontFamily }}>
        {children}
      </Box>
    </Box>
  );
}

export default function ClassDetail() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const tPrimaryText = theme.palette.getContrastText(tPrimary);
  const tPrimaryDarkText = theme.palette.getContrastText(tPrimaryDark);
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary50 = alpha(tSecondary, 0.10);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isFeatureEnabled, tenantConfig } = useTenant();

  const contentEnabled = isFeatureEnabled("instructorClassContent");

  const [cls, setCls] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [, setDashboardData] = useState([]);
  const [instructorName, setInstructorName] = useState("");
  const [, setTitle] = useState("");
  const [, setDate] = useState("");
  const [, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isInstructorForClass, setIsInstructorForClass] = useState(false);

  const scheduleEntries = useMemo(() => {
    const schedule = cls?.schedule;
    if (!schedule) return [];
    if (Array.isArray(schedule)) {
      return schedule.filter((item) => item?.start && item?.end);
    }
    if (schedule?.start && schedule?.end) {
      return [schedule];
    }
    return [];
  }, [cls?.schedule]);

  const formatScheduleEntry = (entry) => {
    if (!entry?.start || !entry?.end) return "";
    const start = new Date(entry.start);
    const end = new Date(entry.end);
    return `${start.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })} · ${start.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })} - ${end.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const [, setCanViewMembers] = useState(true);
  const [myRegistration, setMyRegistration] = useState(null);
  const [dropLoading, setDropLoading] = useState(false);
  const [dropMessage, setDropMessage] = useState("");
  const [dropAcknowledged, setDropAcknowledged] = useState(false);
  const cancellationPolicy = tenantConfig?.trainingCenter?.cancellationPolicy || {};
  const dropNeedsAck = Boolean(cancellationPolicy.requireAcknowledgment);
  const [dropConfirmOpen, setDropConfirmOpen] = useState(false);
  const [sessions, setSessions] = useState([]);

  const canAccessClassContent = Boolean(myRegistration) && contentEnabled;

  const authorities = [
    { value: "AHA", label: "American Heart Association" },
    { value: "STATE", label: "State Authority" },
    { value: "NJEMS", label: "NJ EMS" },
    { value: "NYFIRE", label: "NY Fire" },
  ];
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedAuthority, setSelectedAuthority] = useState("");
  const [exportData, setExportData] = useState([]);

  // Registration wizard state
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerSuccessMsg, setRegisterSuccessMsg] = useState("");

  useEffect(() => {
    if (id) {
      fetchClassById(id).then((c) => {
        setCls(c);
        setTitle(c?.title || "");
        setDate(c?.date || "");

        const isInst = user?.roles?.includes("INSTRUCTOR");
        const isCoord = user?.roles?.includes("COURSE_COORDINATOR");
        const isAdmin = user?.roles?.includes("ADMIN") || user?.roles?.includes("GLOBAL_ADMIN");
        const effectiveInstructorId = c?.leadInstructorId || c?.instructorId;
        const isInstructorForClass = isInst && effectiveInstructorId === user.id;
        const canViewClassMembers = isAdmin || isCoord || isInstructorForClass;

        setCanViewMembers(canViewClassMembers);

        if (effectiveInstructorId) {
          fetchUsersByIds([effectiveInstructorId]).then((users) => {
            if (users.length > 0) {
              setInstructorName(
                users[0].name || users[0].fullName || users[0].username,
              );
            } else {
              setInstructorName(String(effectiveInstructorId));
            }
          });
        }

        if (user?.roles?.includes("STUDENT")) {
          fetchRegistrationsForUser(user.id)
            .then((regs) => {
              const existing = regs.find(
                (reg) => String(reg.classId) === String(c.id),
              );
              if (existing) {
                navigate(`/student-hub/${id}`, { replace: true });
                return;
              }
              setMyRegistration(null);
            })
            .catch(() => setMyRegistration(null));
        } else {
          setMyRegistration(null);
        }

        if (isInstructorForClass) {
          setIsInstructorForClass(true);
          fetchInstructorDashboard(id, user.id).then((data) => {
            setDashboardData(data);
          });
          fetchRegistrationsForClass(id)
            .then((regs) => {
              setRegistrations(regs || []);
              const ids = (regs || []).map((r) => r.userId).filter(Boolean);
              if (ids.length) {
                fetchUsersByIds(ids).then((users) => {
                  setParticipants(users);
                  setExportData(
                    users.map((userItem) => ({
                      id: userItem.id,
                      user: userItem,
                      attended: false,
                      prerequisitesMet: false,
                      examScore: null,
                      passed: false,
                    })),
                  );
                });
              } else {
                setParticipants([]);
                setExportData([]);
              }
            })
            .catch(() => {
              setRegistrations([]);
              setParticipants([]);
              setExportData([]);
            });
        } else if (canViewClassMembers) {
          setIsInstructorForClass(false);
          fetchRegistrationsForClass(id)
            .then((regs) => {
              setRegistrations(regs);
              const ids = regs.map((r) => r.userId);
              if (ids.length) {
                fetchUsersByIds(ids).then(setParticipants);
              }
            })
            .catch(() => {
              setRegistrations([]);
              setParticipants([]);
            });
        } else {
          setIsInstructorForClass(false);
          setRegistrations([]);
          setParticipants([]);
          setDashboardData([]);
        }
      });
      fetchSessionsForCourse(id)
        .then((s) => setSessions(Array.isArray(s) ? s : []))
        .catch(() => setSessions([]));
    }
  }, [id, user, navigate]);

  if (!isFeatureEnabled("classes")) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography color="text.secondary">
          Class detail view is not available for your tenant.
        </Typography>
      </Container>
    );
  }

  const handleRegisterClick = () => {
    setRegisterDialogOpen(true);
    setRegisterSuccessMsg("");
  };

  const handleWizardComplete = (registration) => {
    setMyRegistration(registration || null);
    setRegisterSuccessMsg("Successfully registered for this class!");
    setTimeout(() => {
      setRegisterDialogOpen(false);
      setRegisterSuccessMsg("");
      fetchClassById(cls.id).then(setCls);
    }, 2000);
  };

  const handleDropRequest = () => {
    if (!myRegistration) return;
    setDropMessage("");
    setDropAcknowledged(false);
    setDropConfirmOpen(true);
  };

  const handleConfirmDrop = async () => {
    if (!myRegistration) return;
    setDropLoading(true);
    setDropMessage("");
    try {
      await dropRegistration(cls.id, myRegistration.id, dropAcknowledged);
      setMyRegistration(null);
      setDropMessage("You have dropped this class.");
      setDropConfirmOpen(false);
      fetchClassById(cls.id).then(setCls);
    } catch (error) {
      setDropMessage(
        error.message || "Unable to drop class. Please try again.",
      );
    } finally {
      setDropLoading(false);
    }
  };

  const handleExportChange = (id, field, value) => {
    setExportData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const submitExport = () => {
    if (!selectedAuthority) {
      setMessage("Please select an authority before exporting");
      return;
    }
    exportToAuthority(cls.id, selectedAuthority, exportData).then((res) => {
      setMessage(res && res.success ? "Export completed" : "Export failed");
      setExportDialogOpen(false);
    });
  };

  if (!cls) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const catMeta = getCategoryMeta(cls.category, theme);
  const heroPalette = getHeroPalette(theme);
  const seatProgressColor = getContrastRatio(tSecondary, heroPalette.background) >= 3
    ? tSecondary
    : heroPalette.text;
  const topics = Array.isArray(cls.topics)
    ? cls.topics
      .filter((topic) => typeof topic === "string" && topic.trim())
      .map((topic) => topic.trim())
    : [];
  const numericAvailableSeats = Number(cls.availableSeats);
  const hasKnownAvailability = cls.availableSeats != null
    && Number.isFinite(numericAvailableSeats)
    && numericAvailableSeats >= 0;
  const availableSeats = hasKnownAvailability ? numericAvailableSeats : null;
  const rawTotalSeats = cls.capacity || cls.maxStudents;
  const numericTotalSeats = Number(rawTotalSeats);
  const totalSeats = Number.isFinite(numericTotalSeats) && numericTotalSeats > 0
    ? numericTotalSeats
    : null;
  const takenSeats = hasKnownAvailability && totalSeats
    ? Math.min(totalSeats, Math.max(0, totalSeats - availableSeats))
    : null;
  const seatPct = takenSeats != null && totalSeats
    ? Math.round((takenSeats / totalSeats) * 100)
    : 0;
  const deliveryTypes = [...new Set(
    sessions
      .map((session) => String(session?.sessionType || "").toUpperCase())
      .filter((type) => type === "PHYSICAL" || type === "VIRTUAL"),
  )];
  const classDeliveryFormat = [
    cls.deliveryMethod,
    cls.deliveryFormat,
    cls.format,
  ].find((value) => typeof value === "string" && value.trim())?.trim();
  const sessionDeliveryFormat = deliveryTypes.length > 1
    ? "Mixed"
    : deliveryTypes[0] === "PHYSICAL"
      ? "In-person"
      : deliveryTypes[0] === "VIRTUAL"
        ? "Virtual"
        : null;
  const deliveryDisplay = classDeliveryFormat || sessionDeliveryFormat || "Instructor-Led";

  // derive schedule display
  const firstScheduleDate = scheduleEntries.length > 0 ? scheduleEntries[0] : null;
  const scheduleDisplay = firstScheduleDate ? formatScheduleEntry(firstScheduleDate) : "Dates to be announced";

  // fee display
  let feeDisplay;
  if (cls.feeMode && cls.feeMode !== "FREE_ALL") {
    if (cls.feeMode === "PAID_ALL") {
      feeDisplay = `$${Number(cls.feeAmount || 0).toFixed(2)}`;
    } else if (cls.feeMode === "FREE_IN_AGENCY_PAID_OUT") {
      feeDisplay = `Free (in-agency) / $${Number(cls.feeAmount || 0).toFixed(2)} (out-of-agency)`;
    } else {
      feeDisplay = "$0.00";
    }
  } else {
    feeDisplay = "Free";
  }

  // Status badge for hero
  let statusBadge = null;
  if (cls?.instructorId === user?.id) {
    statusBadge = { label: "Teaching", color: tPrimary, bg: tPrimary10 };
  } else if (myRegistration && myRegistration.enrollmentStatus === "WAITLISTED") {
    statusBadge = { label: "On Waitlist", color: "var(--color-ink-3)", bg: "rgba(107,114,128,.10)" };
  } else if (myRegistration) {
    statusBadge = { label: "Registered", color: tSecondary, bg: tSecondary50 };
  } else if (hasKnownAvailability && availableSeats === 0 && cls.waitlistAvailable) {
    statusBadge = { label: "Waitlist Available", color: "var(--color-ink-3)", bg: "rgba(107,114,128,.10)" };
  } else if (hasKnownAvailability && availableSeats === 0) {
    statusBadge = { label: "Registration Closed", color: DS.danger, bg: DS.danger50 };
  }

  return (
    <Box sx={{ minHeight: "100vh", pt: { xs: "18px", md: "26px" }, pb: "100px" }}>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: "16px", md: "40px" } }}>

        {/* ============ HERO ============ */}
        <Box
          data-testid="class-detail-hero"
          sx={{
            position: "relative", bgcolor: heroPalette.background, color: heroPalette.text,
            borderRadius: "18px", boxShadow: DS.shadowLg, overflow: "hidden", mb: "24px",
          }}
        >

          {/* hero top */}
          <Box sx={{ padding: { xs: "24px", md: "30px 32px 26px" } }}>
            {/* eyebrow */}
            {cls.category && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "7px",
                fontSize: "11px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase",
                color: heroPalette.text, mb: "12px",
              }}>
                <Box component="span" sx={{ width: "7px", height: "7px", borderRadius: "50%", bgcolor: tSecondary }} />
                {cls.category}
              </Box>
            )}

            {/* title */}
            <Typography component="h1" sx={{
              m: 0, fontSize: { xs: "28px", md: "38px" }, fontWeight: 800, letterSpacing: "-.03em",
              lineHeight: 1.04, color: heroPalette.text, fontFamily: "var(--font-display)",
            }}>
              {cls.title}
            </Typography>

            {/* subtitle / description */}
            {cls.description && (
              <Typography sx={{ mt: "12px", fontSize: "16px", color: heroPalette.mutedText, maxWidth: "60ch" }}>
                {cls.description}
              </Typography>
            )}

            {/* tags */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap", mt: "16px" }}>
              {cls.courseNumber && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  fontSize: "12.5px", fontWeight: 600, color: heroPalette.text,
                  bgcolor: heroPalette.chipBackground, border: `1px solid ${heroPalette.chipBorder}`,
                  padding: "5px 11px", borderRadius: "999px",
                  fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-.01em",
                }}>
                  {cls.courseNumber}
                </Box>
              )}
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: "7px",
                fontSize: "12.5px", fontWeight: 600, color: heroPalette.text,
                bgcolor: heroPalette.chipBackground, border: `1px solid ${heroPalette.chipBorder}`,
                padding: "5px 11px", borderRadius: "999px",
              }}>
                <Box sx={{ color: heroPalette.mutedText, display: "inline-flex" }}><IcoCal /></Box>
                {scheduleDisplay}
              </Box>
              {cls.location && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  fontSize: "12.5px", fontWeight: 600, color: heroPalette.text,
                  bgcolor: heroPalette.chipBackground, border: `1px solid ${heroPalette.chipBorder}`,
                  padding: "5px 11px", borderRadius: "999px",
                }}>
                  <Box sx={{ color: heroPalette.mutedText, display: "inline-flex" }}><IcoPin /></Box>
                  {cls.location}
                </Box>
              )}
              {statusBadge && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  fontSize: "12.5px", fontWeight: 600, color: heroPalette.text,
                  bgcolor: heroPalette.chipBackground, border: `1px solid ${heroPalette.chipBorder}`,
                  padding: "5px 11px", borderRadius: "999px",
                }}>
                  {statusBadge.label}
                </Box>
              )}
            </Box>
          </Box>

          {/* fact strip */}
          <Box component="section" role="region" aria-label="Class facts" sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
            borderTop: `1px solid ${heroPalette.line}`,
          }}>
            <FactItem palette={heroPalette} icon={<IcoUser />} label="Format">{deliveryDisplay}</FactItem>
            <FactItem palette={heroPalette} icon={<IcoGrad />} label="Instructor">{instructorName || "TBD"}</FactItem>
            <FactItem palette={heroPalette} icon={<IcoClock />} label="Duration">{cls.duration ? `${cls.duration} days` : "TBD"}</FactItem>
            <FactItem palette={heroPalette} icon={<IcoUsers />} label="Seats">
              <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <Box sx={{ fontSize: "15px", fontWeight: 700, color: heroPalette.text, letterSpacing: "-.01em" }}>
                  {hasKnownAvailability ? (
                    <>{availableSeats} open <Box component="span" sx={{ fontSize: "12px", fontWeight: 500, color: heroPalette.mutedText }}>/ {totalSeats || "—"}</Box></>
                  ) : "Availability TBD"}
                </Box>
                {hasKnownAvailability && totalSeats && (
                  <Box sx={{ height: "5px", borderRadius: "999px", bgcolor: heroPalette.line, overflow: "hidden" }}>
                    <Box data-testid="seat-progress-fill" sx={{ height: "100%", width: `${seatPct}%`, borderRadius: "999px", bgcolor: seatProgressColor }} />
                  </Box>
                )}
              </Box>
            </FactItem>
            <FactItem palette={heroPalette} icon={<IcoTag />} label="Fee">
              <Box sx={{ fontSize: "15px", fontWeight: 700, color: heroPalette.text, letterSpacing: "-.01em" }}>
                {feeDisplay}
              </Box>
            </FactItem>
          </Box>
        </Box>

        {/* Instructor tabs */}
        {isInstructorForClass && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, boxShadow: DS.shadowSm, mb: 4 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} textColor="primary" indicatorColor="primary" sx={{ px: 2, overflow: "hidden" }}>
              <Tab label="Details" value={0} sx={{ textTransform: "none" }} />
              {contentEnabled && (
                <Tab label={`Roster (${registrations.length})`} value={1} sx={{ textTransform: "none" }} />
              )}
            </Tabs>
          </Box>
        )}

        {/* ============ GRID ============ */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 352px" }, gap: "28px", alignItems: "start" }}>

          {/* MAIN */}
          <Box>
            {isInstructorForClass && activeTab === 1 ? (
              <Card elevation={0} sx={{ bgcolor: "#fff", p: 4, borderRadius: 3, boxShadow: DS.shadowSm }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography variant="h6" sx={{ color: tPrimary, fontWeight: 700 }}>
                    Class Roster
                  </Typography>
                  <Typography sx={{ color: DS.ink2 }}>
                    {registrations.length} enrolled
                  </Typography>
                </Box>
                {participants.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Username</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants.map((student) => {
                          const registration = registrations.find(
                            (reg) => String(reg.userId) === String(student.id),
                          );
                          return (
                            <TableRow key={student.id}>
                              <TableCell>{student.name || student.username || "Unknown"}</TableCell>
                              <TableCell>{student.username || student.email || "—"}</TableCell>
                              <TableCell>{registration?.status || "Enrolled"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">No students are enrolled in this class yet.</Typography>
                )}
              </Card>
            ) : (
              <Stack spacing={4}>
                {/* About */}
                {cls.description && (
                  <Box>
                    <SectionHeader icon={<IcoInfo />}>
                      About This Course
                    </SectionHeader>
                    <Box sx={{ bgcolor: "#fff", border: `1px solid ${DS.line}`, borderRadius: "16px", padding: "20px 24px 22px" }}>
                      <Typography sx={{ fontSize: "15px", color: DS.ink2, lineHeight: 1.65, maxWidth: "66ch" }}>
                        {cls.description}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* What You'll Cover */}
                {topics.length > 0 && (
                  <Box component="section" aria-labelledby="course-topics-heading">
                    <SectionHeader icon={<IcoCheck />} id="course-topics-heading">
                      What You&apos;ll Cover
                    </SectionHeader>
                    <Box sx={{
                      bgcolor: "#fff",
                      border: `1px solid ${DS.line}`,
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}>
                      {topics.map((topic, index) => (
                        <Box
                          key={`${topic}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "13px 16px",
                            borderBottom: `1px solid ${DS.line2}`,
                            fontSize: "13.5px",
                            fontWeight: 600,
                            color: DS.ink2,
                            "&:last-child": { borderBottom: 0 },
                          }}
                        >
                          <Box sx={{ color: catMeta.accent, display: "inline-flex", flexShrink: 0 }}>
                            <IcoCheck />
                          </Box>
                          {topic}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Prerequisites */}
                {(() => {
                  const prereqsText = (cls.prereqs || "").trim();
                  const restrictionsText = (cls.restrictions || "").trim();
                  const hasPrereqs = prereqsText !== "" && prereqsText.toLowerCase() !== "none";
                  const hasRestrictions = restrictionsText !== "" && restrictionsText.toLowerCase() !== "none";
                  return (hasPrereqs || hasRestrictions) && (
                  <Box
                    component="section"
                    role="region"
                    aria-labelledby="registration-prerequisites-heading"
                    data-testid="registration-prerequisites-callout"
                    sx={{
                      position: "relative",
                      bgcolor: alpha(theme.palette.error.main, 0.06),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`,
                      borderRadius: "16px",
                      padding: "20px 24px 22px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "6px" }}>
                      <Box sx={{ color: theme.palette.error.main, display: "inline-flex" }}><IcoWarn /></Box>
                      <Typography
                        component="h2"
                        id="registration-prerequisites-heading"
                        sx={{
                          m: 0,
                          fontSize: "17px",
                          fontWeight: 700,
                          fontFamily: theme.typography.h2.fontFamily,
                          letterSpacing: "-.01em",
                          color: DS.ink,
                        }}
                      >
                        Registration Prerequisites
                      </Typography>
                    </Box>
                    <Typography sx={{ m: 0, mb: "14px", fontSize: "13.5px", fontWeight: 500, color: DS.ink2 }}>
                      You must have completed the following before enrolling in this course.
                    </Typography>
                    <Box
                      data-testid="registration-prerequisite-rows"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        bgcolor: "#fff",
                        border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`,
                        borderRadius: "12px",
                        padding: "8px",
                      }}
                    >
                      {hasPrereqs && (
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: "12px", bgcolor: "#fff",
                          border: `1px solid ${DS.line}`, borderRadius: "11px", padding: "11px 15px",
                        }}>
                          <Box sx={{
                            width: "24px", height: "24px", borderRadius: "7px", flexShrink: 0,
                            display: "grid", placeItems: "center", bgcolor: catMeta.bg, color: catMeta.accent,
                            fontSize: "12px", fontWeight: 700,
                          }}>1</Box>
                          <Box sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink }}>{cls.prereqs}</Box>
                        </Box>
                      )}
                      {hasRestrictions && (
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: "12px", bgcolor: "#fff",
                          border: `1px solid ${DS.line}`, borderRadius: "11px", padding: "11px 15px",
                        }}>
                          <Box sx={{
                            width: "24px", height: "24px", borderRadius: "7px", flexShrink: 0,
                            display: "grid", placeItems: "center", bgcolor: DS.danger50, color: DS.danger,
                            fontSize: "12px", fontWeight: 700,
                          }}><IcoWarn /></Box>
                          <Box sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink }}>{cls.restrictions}</Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  );
                })()}

                {/* Course Details */}
                <Box>
                  <SectionHeader icon={<IcoList />}>
                    Course Details
                  </SectionHeader>
                  <Box sx={{ bgcolor: "#fff", border: `1px solid ${DS.line}`, borderRadius: "16px", boxShadow: DS.shadowSm, overflow: "hidden" }}>
                    {cls.courseNumber && (
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                        padding: "14px 22px", borderBottom: `1px solid ${DS.line2}`, gap: { xs: "4px", sm: 0 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                          <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoTag /></Box>
                          Course Number
                        </Box>
                        <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, fontFamily: "'JetBrains Mono', monospace" }}>
                          {cls.courseNumber}
                        </Box>
                      </Box>
                    )}
                    {deliveryDisplay && (
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                        padding: "14px 22px", borderBottom: `1px solid ${DS.line2}`, gap: { xs: "4px", sm: 0 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                          <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoUser /></Box>
                          Delivery Format
                        </Box>
                        <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink }}>
                          {deliveryDisplay}
                        </Box>
                      </Box>
                    )}
                    {cls.location && (
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                        padding: "14px 22px", borderBottom: `1px solid ${DS.line2}`, gap: { xs: "4px", sm: 0 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                          <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoPin /></Box>
                          Location
                        </Box>
                        <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink }}>{cls.location}</Box>
                      </Box>
                    )}
                    <Box sx={{
                      display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                      padding: "14px 22px", borderBottom: `1px solid ${DS.line2}`, gap: { xs: "4px", sm: 0 },
                    }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                        <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoCal /></Box>
                        Schedule
                      </Box>
                      <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink }}>
                        {scheduleEntries.length > 0 ? (
                          <Stack spacing={0.5}>
                            {scheduleEntries.map((entry, idx) => (
                              <Box key={idx}>{formatScheduleEntry(entry)}</Box>
                            ))}
                          </Stack>
                        ) : (
                          "Dates to be announced"
                        )}
                      </Box>
                    </Box>
                    {(cls.duration || cls.totalHours) && (
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                        padding: "14px 22px", borderBottom: `1px solid ${DS.line2}`, gap: { xs: "4px", sm: 0 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                          <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoClock /></Box>
                          Total Hours
                        </Box>
                        <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink }}>
                          {cls.totalHours ? `${cls.totalHours} hrs` : ""} {cls.duration ? <Box component="span" sx={{ color: DS.ink3, fontWeight: 500 }}>· {cls.duration} days</Box> : ""}
                        </Box>
                      </Box>
                    )}
                    {cls.certification && (
                      <Box sx={{
                        display: "grid", gridTemplateColumns: { xs: "1fr", sm: "190px 1fr" }, alignItems: "center",
                        padding: "14px 22px", gap: { xs: "4px", sm: 0 },
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", fontWeight: 600, color: DS.ink3 }}>
                          <Box sx={{ color: DS.ink4, display: "inline-flex" }}><IcoCert /></Box>
                          Certification
                        </Box>
                        <Box sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink }}>{cls.certification}</Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Instructor */}
                {instructorName && (
                  <Box>
                    <SectionHeader icon={<IcoUser />}>
                      Instructor
                    </SectionHeader>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: "16px", bgcolor: "#fff",
                      border: `1px solid ${DS.line}`, borderRadius: "16px", padding: "18px 20px", boxShadow: DS.shadowSm,
                    }}>
                      <Box sx={{
                        width: "52px", height: "52px", borderRadius: "50%", flexShrink: 0,
                        display: "grid", placeItems: "center", bgcolor: tPrimary, color: tPrimaryText,
                        fontSize: "18px", fontWeight: 700, letterSpacing: "-.02em",
                      }}>
                        {instructorName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: "15px", fontWeight: 700, color: DS.ink }}>{instructorName}</Box>
                        <Box sx={{ fontSize: "13px", color: DS.ink3, mt: "2px" }}>Lead Instructor</Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Sessions */}
                {sessions.length > 0 && canAccessClassContent ? (
                  <Card elevation={0} sx={{ bgcolor: "#fff", p: 4, borderRadius: 3, boxShadow: DS.shadowSm }}>
                    <Typography variant="h6" sx={{ color: tPrimary, fontWeight: 700, mb: 2 }}>
                      Sessions
                    </Typography>
                    <Stack spacing={1}>
                      {sessions.map((session) => {
                        const sessionDateLabel = formatLocalDate(session.sessionDate, {
                          weekday: "short", year: "numeric", month: "short", day: "numeric",
                        });
                        return (
                        <Box key={session.id} display="flex" alignItems="center" justifyContent="space-between" sx={{
                          p: 2, borderRadius: 2, bgcolor: "#fff", border: `1px solid ${DS.line2}`,
                        }}>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography sx={{ fontWeight: 600, color: DS.ink }}>
                                {session.title || `Session ${session.sessionNumber || session.id}`}
                              </Typography>
                              <Chip
                                label={session.enabled === false ? "Disabled" : "Enabled"}
                                size="small"
                                sx={{
                                  bgcolor: session.enabled === false ? "rgba(148,163,184,0.12)" : tSecondary50,
                                  color: session.enabled === false ? "var(--color-ink-2)" : tSecondary,
                                  fontWeight: 700, fontSize: "0.7rem",
                                }}
                              />
                            </Box>
                            {sessionDateLabel && (
                              <Typography sx={{ fontSize: "0.875rem", color: DS.ink2 }}>
                                {sessionDateLabel}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={1}>
                            {(user?.roles?.includes("INSTRUCTOR") ||
                              user?.roles?.includes("COURSE_COORDINATOR") ||
                              user?.roles?.includes("ADMIN") ||
                              user?.roles?.includes("GLOBAL_ADMIN")) && (
                              <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate(`/course-hub/${id}/session-content`)}
                                sx={{ borderColor: tPrimary, color: tPrimary, textTransform: "none", fontWeight: 600 }}
                              >
                                Manage Content
                              </Button>
                            )}
                            {canAccessClassContent && (
                              <Button
                                variant="contained"
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate(`/student-hub/${id}`)}
                                disabled={session.enabled === false}
                                sx={{
                                  bgcolor: session.enabled === false ? "rgba(115,119,129,0.14)" : tSecondary,
                                  color: session.enabled === false ? "var(--color-ink-3)" : "#fff",
                                  textTransform: "none", fontWeight: 600,
                                }}
                              >
                                {session.enabled === false ? "Session Disabled" : "Join Session"}
                              </Button>
                            )}
                          </Box>
                        </Box>
                        );
                      })}
                    </Stack>
                  </Card>
                ) : sessions.length > 0 ? (
                  <Card elevation={0} sx={{ bgcolor: "#fff", p: 4, borderRadius: 3, boxShadow: DS.shadowSm }}>
                    <Typography variant="h6" sx={{ color: tPrimary, fontWeight: 700, mb: 2 }}>
                      Session Details Locked
                    </Typography>
                    <Typography sx={{ color: DS.ink2, lineHeight: 1.75 }}>
                      Register for this class to unlock session details, join links, and class content.
                    </Typography>
                  </Card>
                ) : null}
              </Stack>
            )}
          </Box>

          {/* SIDEBAR */}
          <Box component="aside" aria-label="Registration summary" sx={{ position: { xs: "static", lg: "sticky" }, top: { lg: "84px" }, display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Registration Summary */}
            <Box sx={{ bgcolor: "#fff", border: `1px solid ${DS.line}`, borderRadius: "18px", boxShadow: DS.shadowMd, overflow: "hidden" }}>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "18px 22px 16px", borderBottom: `1px solid ${DS.line2}`,
              }}>
                <Typography component="h2" sx={{ m: 0, fontSize: "16px", fontWeight: 700, letterSpacing: "-.01em", fontFamily: "var(--font-display)" }}>
                  Registration Summary
                </Typography>
                {!hasKnownAvailability ? (
                  <Box sx={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                    color: DS.grey, bgcolor: DS.greyBg, padding: "4px 9px", borderRadius: "999px",
                  }}>Availability TBD</Box>
                ) : availableSeats > 0 ? (
                  <Box sx={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                    color: tSecondary, bgcolor: tSecondary50, padding: "4px 9px", borderRadius: "999px",
                  }}>Open</Box>
                ) : cls.waitlistAvailable ? (
                  <Box sx={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                    color: "var(--color-ink-3)", bgcolor: "rgba(107,114,128,.10)", padding: "4px 9px", borderRadius: "999px",
                  }}>Waitlist</Box>
                ) : (
                  <Box sx={{
                    fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                    color: DS.danger, bgcolor: DS.danger50, padding: "4px 9px", borderRadius: "999px",
                  }}>Full</Box>
                )}
              </Box>

              <Box sx={{ padding: "18px 22px 20px" }}>
                {/* Selected Course */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: "13px", bgcolor: "#fff",
                  border: `1px solid ${DS.line}`, borderRadius: "13px", padding: "13px 15px", mb: "18px",
                }}>
                  <Box sx={{
                    width: "40px", height: "40px", borderRadius: "10px", flexShrink: 0,
                    display: "grid", placeItems: "center", bgcolor: tPrimary10, color: tPrimary,
                  }}>
                    <IcoGrad />
                  </Box>
                  <Box>
                    <Box sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DS.ink3 }}>
                      Selected Course
                    </Box>
                    <Box sx={{ fontSize: "15px", fontWeight: 700, color: DS.ink, letterSpacing: "-.01em", mt: "2px" }}>
                      {cls.title}
                    </Box>
                  </Box>
                </Box>

                {/* Detail lines */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: "18px", "& > div": { flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: "4px", sm: 0 } } }}>
                  {deliveryDisplay && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: `1px dashed ${DS.line}`, fontSize: "13.5px" }}>
                      <Box sx={{ color: DS.ink3, fontWeight: 500 }}>Schedule Format</Box>
                      <Box sx={{ fontWeight: 700, color: DS.ink }}>{deliveryDisplay}</Box>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: `1px dashed ${DS.line}`, fontSize: "13.5px" }}>
                    <Box sx={{ color: DS.ink3, fontWeight: 500 }}>Instructor</Box>
                    <Box sx={{ fontWeight: 700, color: DS.ink }}>{instructorName || "TBD"}</Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: `1px dashed ${DS.line}`, fontSize: "13.5px" }}>
                    <Box sx={{ color: DS.ink3, fontWeight: 500 }}>Seats Remaining</Box>
                    <Box sx={{ fontWeight: 700, color: DS.ink }}>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: "5px", fontWeight: 700, color: DS.ink }}>
                        {hasKnownAvailability ? (
                          <>
                            <Box component="span" sx={{ color: tSecondary, fontVariantNumeric: "tabular-nums" }}>{availableSeats}</Box>
                            <Box component="span" sx={{ fontSize: "12px", fontWeight: 500, color: DS.ink3 }}>of {totalSeats || "—"}</Box>
                          </>
                        ) : "Availability TBD"}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", fontSize: "13.5px" }}>
                    <Box sx={{ color: DS.ink3, fontWeight: 500 }}>Fee</Box>
                    <Box sx={{ fontWeight: 700, color: cls.feeMode && cls.feeMode !== "FREE_ALL" ? DS.ink : tSecondary }}>
                      {feeDisplay}
                    </Box>
                  </Box>
                </Box>

                {registerSuccessMsg && (
                  <Box sx={{
                    mb: 2, p: 1.5, borderRadius: 2, bgcolor: "var(--color-success-bg)", border: "1px solid", borderColor: "success.main",
                  }}>
                    <Typography variant="body2" color="success.dark" fontWeight={600}>
                      {registerSuccessMsg}
                    </Typography>
                  </Box>
                )}

                <Stack spacing={1.5}>
                  {user?.roles?.includes("STUDENT") && myRegistration ? (
                    <>
                      <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        onClick={handleDropRequest}
                        disabled={dropLoading}
                        startIcon={<DeleteIcon />}
                        sx={{
                          py: 1.75, borderRadius: "14px", fontWeight: 700, fontSize: "0.9375rem",
                          textTransform: "none", boxShadow: "0 4px 14px rgba(186,26,26,0.2)",
                        }}
                      >
                        {dropLoading
                          ? "Dropping..."
                          : myRegistration.enrollmentStatus === "WAITLISTED"
                            ? "Leave Waitlist"
                            : "Drop Class"}
                      </Button>
                      <Typography sx={{
                        color: myRegistration.enrollmentStatus === "WAITLISTED" ? "var(--color-ink-3)" : "success.main",
                        fontWeight: 600,
                      }}>
                        {myRegistration.enrollmentStatus === "WAITLISTED"
                          ? "You are on the waitlist for this class."
                          : "You are registered for this class."}
                      </Typography>
                    </>
                  ) : user?.roles?.includes("STUDENT") && hasKnownAvailability && availableSeats > 0 ? (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleRegisterClick}
                      disabled={registerDialogOpen}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        bgcolor: tPrimary, color: tPrimaryText, py: 1.75, borderRadius: "14px", fontWeight: 700,
                        fontSize: "0.9375rem", textTransform: "none",
                        boxShadow: `0 8px 18px -8px ${alpha(tPrimary, 0.55)}`,
                        "&:hover": { bgcolor: tPrimaryDark, color: tPrimaryDarkText, boxShadow: `0 12px 24px -8px ${alpha(tPrimary, 0.6)}` },
                      }}
                    >
                      Complete Registration
                    </Button>
                  ) : user?.roles?.includes("STUDENT") && hasKnownAvailability && availableSeats === 0 && cls.waitlistAvailable ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleRegisterClick}
                      disabled={registerDialogOpen}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        py: 1.75, borderRadius: "14px", fontWeight: 700, fontSize: "0.9375rem",
                        textTransform: "none", borderColor: "var(--color-ink-3)", color: "var(--color-ink-3)", bgcolor: "#fff",
                        "&:hover": { bgcolor: "rgba(107,114,128,.06)" },
                      }}
                    >
                      Join Waitlist
                    </Button>
                  ) : user?.roles?.includes("STUDENT") && hasKnownAvailability && availableSeats === 0 ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled
                      sx={{
                        py: 1.75, borderRadius: "14px", fontWeight: 700, fontSize: "0.9375rem",
                        textTransform: "none", borderColor: "rgba(0,0,0,0.12)", color: "#737781",
                      }}
                    >
                      Class Full
                    </Button>
                  ) : null}

                  {isInstructorForClass && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setExportDialogOpen(true)}
                      sx={{
                        py: 1.75, borderRadius: "14px", fontWeight: 700, fontSize: "0.9375rem",
                        textTransform: "none", borderColor: DS.line, color: tPrimary, bgcolor: "#fff",
                        "&:hover": { bgcolor: DS.surface2 },
                      }}
                    >
                      Export Roster
                    </Button>
                  )}

                  {dropMessage && (
                    <Typography color="error" sx={{ fontWeight: 600 }}>
                      {dropMessage}
                    </Typography>
                  )}
                </Stack>

                <Typography component="div" sx={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
                  mt: "14px", fontSize: "11px", fontWeight: 600, letterSpacing: ".06em",
                  textTransform: "uppercase", color: DS.ink4,
                }}>
                  <Box sx={{ color: tSecondary, display: "inline-flex" }}><IcoLock /></Box>
                  Secure Registration Platform
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Export Dialog */}
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ pb: 1 }}>Export to Certificate Authority</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select Authority
              </Typography>
              <Select fullWidth value={selectedAuthority} onChange={(e) => setSelectedAuthority(e.target.value)} displayEmpty>
                <MenuItem value="" disabled>Select an authority...</MenuItem>
                {authorities.map((a) => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Attended</TableCell>
                    <TableCell>Prerequisites</TableCell>
                    <TableCell>Exam Score</TableCell>
                    <TableCell>Passed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportData.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.user?.username}</TableCell>
                      <TableCell>
                        <Checkbox checked={d.attended || false} onChange={(e) => handleExportChange(d.id, "attended", e.target.checked)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Checkbox checked={d.prerequisitesMet || false} onChange={(e) => handleExportChange(d.id, "prerequisitesMet", e.target.checked)} size="small" />
                      </TableCell>
                      <TableCell>
                        <TextField type="number" value={d.examScore || ""} onChange={(e) => handleExportChange(d.id, "examScore", e.target.value ? Number(e.target.value) : null)} size="small" sx={{ width: "80px" }} />
                      </TableCell>
                      <TableCell>
                        <Checkbox checked={d.passed || false} onChange={(e) => handleExportChange(d.id, "passed", e.target.checked)} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setExportDialogOpen(false)} variant="outlined">Cancel</Button>
            <Button variant="contained" onClick={submitExport} disabled={!selectedAuthority || exportData.length === 0}>Export</Button>
          </DialogActions>
        </Dialog>

        {/* Drop Confirmation Dialog */}
        <Dialog open={dropConfirmOpen} onClose={() => !dropLoading && setDropConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: 420 } }}>
          <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 700, color: tPrimary }}>
            Confirm Drop
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            <Typography sx={{ color: DS.ink2 }}>
              Are you sure you want to drop <strong>{cls?.title}</strong>? This action will remove your registration from the class.
            </Typography>
            {dropNeedsAck && (
              <Box sx={{ mt: 2 }}>
                {cancellationPolicy.statement && (
                  <Typography variant="body2" sx={{ color: DS.ink2, whiteSpace: "pre-wrap", mb: 1 }}>
                    {cancellationPolicy.statement}
                  </Typography>
                )}
                <FormControlLabel
                  control={<Checkbox checked={dropAcknowledged} onChange={(e) => setDropAcknowledged(e.target.checked)} />}
                  label="I have read and accept the cancellation policy"
                />
              </Box>
            )}
            {dropMessage && (
              <Box sx={{
                mt: 2, p: 2, borderRadius: 2,
                backgroundColor: dropMessage.includes("dropped") ? "success.lighter" : "error.lighter",
                border: "1px solid",
                borderColor: dropMessage.includes("dropped") ? "success.main" : "error.main",
              }}>
                <Typography variant="body2" color={dropMessage.includes("dropped") ? "success.dark" : "error.dark"}>
                  {dropMessage}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button onClick={() => setDropConfirmOpen(false)} variant="outlined" disabled={dropLoading}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleConfirmDrop} disabled={dropLoading || (dropNeedsAck && !dropAcknowledged)}>
              {dropLoading ? <CircularProgress size={20} color="inherit" /> : "Drop Class"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Registration Wizard */}
        <RegistrationWizard
          open={registerDialogOpen}
          onClose={() => setRegisterDialogOpen(false)}
          classData={cls}
          onComplete={handleWizardComplete}
          currentUser={user}
          tenantConfig={tenantConfig}
        />
      </Box>
    </Box>
  );
}
